import type { CompanyArticles } from "@/modules/companies/api/types"
import type { I18nLocale } from "@/modules/i18n/types"

import { useQuery } from "@tanstack/react-query"

import {
  DEFAULT_QUERY_CACHE_TIME as QUERY_CACHE_TIME,
  DEFAULT_QUERY_STALE_TIME as QUERY_STALE_TIME,
} from "@/modules/api/constants"
import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { COMPANY_ARTICLES_QUERY_KEY, getCompanyArticles } from "@/modules/companies/api/endpoints/companyArticles"
import { useCompanyFetch } from "@/modules/companies/hooks/useCompanyFetch"
import { stripTagClientSide } from "@/modules/companies/utils/stripTagClientSide"
import { DEFAULT_LOCALE } from "@/modules/locales/constants"

export type CompanyBoostedArticlesFetch = {
  dataCompanyBoostedArticles?: CompanyArticles
  isErrorCompanyBoostedArticles: boolean
  isLoadingCompanyBoostedArticles: boolean
  isSuccessCompanyBoostedArticles: boolean
}

export const useCompanyBoostedArticlesFetch = (): CompanyBoostedArticlesFetch => {
  const { dataCompany } = useCompanyFetch()
  const { dataCareerCenter } = useCareerCenterFetch()
  const locale = (dataCompany?.page.locale as I18nLocale) || DEFAULT_LOCALE

  const schoolIds = dataCareerCenter && dataCareerCenter.intranet ? [dataCareerCenter.id] : null

  const {
    data: dataCompanyBoostedArticles,
    isError: isErrorCompanyBoostedArticles,
    isPending: isLoadingCompanyBoostedArticles,
    isSuccess: isSuccessCompanyBoostedArticles,
  } = useQuery({
    enabled: !!dataCompany?.id,
    gcTime: QUERY_CACHE_TIME,
    queryFn: () => {
      return getCompanyArticles({
        params: {
          boosted_only: true,
          company_id: dataCompany?.id as string,
          locales: [locale],
          page_limit: 1,
          page_offset: 0,
          school_ids: schoolIds || undefined,
        },
        sanitize: stripTagClientSide,
      })
    },
    queryKey: [COMPANY_ARTICLES_QUERY_KEY, dataCompany?.id, locale, "boosted_only", schoolIds],
    staleTime: QUERY_STALE_TIME,
  })

  return {
    dataCompanyBoostedArticles,
    isErrorCompanyBoostedArticles,
    isLoadingCompanyBoostedArticles,
    isSuccessCompanyBoostedArticles,
  }
}
